import { useState } from "react";
import { useNavigate } from "react-router-dom";
import StepIndicator from "./StepIndicator";
import SummaryStep from "./SummaryStep";
import DetailsStep from "./DetailsStep";
import SuccessStep from "./SuccessStep";
import OrderRecap from "./OrderRecap";
import styles from "./checkout.module.css";
import {
  clientSubscribeToPlan,
  clientSubscribeToPlanSignupCheckout,
} from "../../../../services/clientCalls";
import { errorHandler } from "../../../../utils";
import { markAttributionClaimed } from "../../../../utils/attribution";
import { rememberPendingPurchase, trackSignUp } from "../../../../utils/analyticsEvents";

const TRUST_ITEMS_DESKTOP = [
  { icon: "🔒", text: "Payments secured by Paystack" },
  { icon: "🛡️", text: "30-day rematch guarantee" },
  { icon: "💳", text: "No card stored after payment" },
  { icon: "📧", text: "Account only created on success" },
];

const TRUST_ITEMS_MOBILE = [
  { icon: "🔒", text: "Secured by Paystack" },
  { icon: "🛡️", text: "30-day rematch" },
  { icon: "📧", text: "Account on success only" },
];

const CheckoutFlow = ({
  product,
  productType = "pack",
  currency = "USD",
  onBack = () => {},
}) => {
  const navigate = useNavigate();

  const [step, setStep] = useState("summary");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryIso: "NG",
    dialCode: "+234",
    location: "",
    locationCountryIso: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verifiedData, setVerifiedData] = useState(null);

  const handleFormChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setError(null);
  };

  // Step 2 → 3: initialize payment, then open Paystack
  const handleSubscription = async () => {
    if (!product) return;
    setLoading(true);
    try {
      const payload = {
        firstName: form?.firstName,
        lastName: form?.lastName,
        email: form?.email,
        phone: form?.dialCode + form?.phone,
        location: form?.location,
        planId: product?.id,
        planType: product?.type,
        currency: product?.currency,
      };

      const res = await clientSubscribeToPlanSignupCheckout(payload);
      markAttributionClaimed();
      trackSignUp({ method: "checkout", userType: "client", email: form?.email });
      rememberPendingPurchase({
        item_name: product?.name || "credits",
        item_category: productType === "annual" ? "annual" : "topup",
        value: product?.price,
        currency,
      });

      // Paystack authorization_url — check common nesting patterns
      const authUrl =
        res?.data?.data?.authorization_url ??
        res?.data?.data?.data?.authorization_url ??
        res?.data?.data?.data?.data?.authorization_url;

      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setLoading(false);
    }
  };

  const handleDashboard = () => {
    navigate("/login");
  };

  const showSidebar = step !== "success" && step !== "error";

  return (
    <div className={styles.checkoutRoot}>
      <div className={styles.topbar}>
        <button
          onClick={step === "success" ? undefined : onBack}
          disabled={step === "success" || step === "verifying"}
          className={styles.backLink}
        >
          ← Back to pricing
        </button>
        <div className={styles.brand}>
          <span style={{ fontSize: 18 }}>⚡</span> AwaSource
        </div>
        <div className={styles.secureBadge}>
          <span>🔒</span> Secured by Paystack
        </div>
      </div>

      <div className={styles.page}>
        <div>
          {step !== "success" && step !== "error" && (
            <StepIndicator currentStep={step} />
          )}

          {step === "summary" && (
            <SummaryStep
              product={product}
              productType={productType}
              currency={currency}
              onContinue={() => setStep("details")}
              onBack={onBack}
            />
          )}
          {step === "details" && (
            <DetailsStep
              form={form}
              onChange={handleFormChange}
              onSubmit={handleSubscription}
              loading={loading}
              error={error}
              onBack={() => {
                setStep("summary");
                setError(null);
              }}
            />
          )}
          {step === "success" && (
            <SuccessStep
              product={product}
              productType={productType}
              currency={currency}
              verifiedData={verifiedData}
              onDashboard={handleDashboard}
            />
          )}
        </div>

        {showSidebar && (
          <>
            <div className={styles.desktopOnly}>
              <OrderRecap
                product={product}
                productType={productType}
                currency={currency}
              />
              <div className={styles.trustCard}>
                <div className={styles.trustList}>
                  {TRUST_ITEMS_DESKTOP.map((t) => (
                    <div key={t.text} className={styles.trustRow}>
                      <span className={styles.trustIcon}>{t.icon}</span>
                      <span className={styles.trustText}>{t.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.mobileOnly}>
              <div className={styles.trustCard} style={{ marginTop: 0 }}>
                <div className={styles.trustRowMobile}>
                  {TRUST_ITEMS_MOBILE.map((t) => (
                    <div key={t.text} className={styles.trustItemMobile}>
                      <span className={styles.trustIcon}>{t.icon}</span>
                      <span className={styles.trustText}>{t.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutFlow;
